import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { randomizeScheme, setOverlay } from 'store/sessionSlice';
import Player from 'components/Player';

const NavBar = () => {
  const { username, scheme, overlay } = useSelector((s) => s.session);
  const { mixtape } = useSelector((s) => s.player);
  const dispatch = useDispatch();
  const [expanded, setExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const ref = useRef(null);
  // Suppress the intro blur when the app opens directly on a story load
  // (e.g. /community), matching the splash title in Page01.
  const splashRef = useRef(
    (() => {
      const start = window.location.pathname.replace(/^\/+/, '');
      return start === '' || start === 'tripreport' || !isNaN(parseInt(start, 10));
    })()
  );

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', scheme.bgOuter);
  }, [scheme.bgOuter]);

  // Foldit ships a COLR table, so Chrome paints its grey palette over our CSS
  // color. Override every palette entry to the scheme color (currentColor is
  // unsupported here, so it's rebuilt whenever the scheme changes).
  useEffect(() => {
    const id = 'wordmark-palette';
    let style = document.getElementById(id);
    if (!style) {
      style = document.createElement('style');
      style.id = id;
      document.head.appendChild(style);
    }
    const stops = Array.from({ length: 16 }, (_, i) => `${i} ${scheme.text}`);
    style.textContent = `@font-palette-values --wordmark { font-family: Foldit; override-colors: ${stops.join(', ')}; }`;
  }, [scheme.text]);

  useEffect(() => {
    if (!expanded) return undefined;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setExpanded(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [expanded]);

  const collapsedWidth = mixtape ? '5rem' : '2.2rem';

  return (
    <div
      ref={ref}
      className={`navbar-slide-in navbar-pill${splashRef.current ? ' splash' : ''}`}
      style={{
        position: 'fixed',
        top: '0.4rem',
        left: '0.4rem',
        zIndex: 20,
        boxSizing: 'border-box',
        height: '2.2rem',
        display: 'flex',
        alignItems: 'center',
        justifyContent: expanded ? 'flex-start' : 'center',
        gap: '0.75rem',
        padding: expanded ? '0 1rem' : mixtape ? '0 0.6rem' : '0',
        borderRadius: '5rem',
        background: 'rgba(0, 0, 0, 0.55)',
        border: `1px solid ${scheme.text}`,
        backdropFilter: 'blur(6px)',
        color: scheme.text,
        fontFamily: 'c64Mono',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        width: expanded ? 'auto' : collapsedWidth,
        maxWidth: expanded
          ? isMobile
            ? 'calc(100vw - 0.8rem)'
            : mixtape
              ? '44rem'
              : '34rem'
          : collapsedWidth,
        transition: 'max-width 0.4s ease'
      }}
    >
      <span
        className="glitch"
        data-text={expanded && !isMobile ? 'cyberdeck' : 'C'}
        onClick={() => setExpanded((e) => !e)}
        title={expanded ? 'Collapse' : 'Expand'}
        style={{
          position: 'relative',
          top: '0.144rem',
          flex: '0 0 auto',
          fontFamily: 'Foldit',
          fontWeight: 900,
          fontSize: expanded && !isMobile ? '1.4rem' : '1.6rem',
          lineHeight: 1,
          cursor: 'pointer',
          color: scheme.text,
          fontPalette: '--wordmark',
          '--glitch-before': scheme.text,
          '--glitch-after': scheme.text
        }}
      >
        {expanded && !isMobile ? 'cyberdeck' : 'C'}
      </span>
      {expanded && username && (
        <span
          style={{ flex: '0 0 auto', color: 'white' }}
        >{`//${username}`}</span>
      )}
      {expanded && (
        <button
          title="Randomize color scheme"
          onClick={() => dispatch(randomizeScheme())}
          style={{
            flex: '0 0 auto',
            cursor: 'pointer',
            background: 'transparent',
            border: 'none',
            color: scheme.text,
            fontSize: '1.2rem',
            lineHeight: 1,
            padding: 0,
            display: 'flex',
            alignItems: 'center'
          }}
        >
          <svg width="28" height="19" viewBox="0 0 28 19" aria-hidden="true">
            <defs>
              <mask id="rgb-outer-r">
                <rect width="28" height="19" fill="#fff" />
                <circle cx="18" cy="7" r="6" fill="#000" />
                <circle cx="14" cy="12" r="6" fill="#000" />
              </mask>
              <mask id="rgb-outer-g">
                <rect width="28" height="19" fill="#fff" />
                <circle cx="10" cy="7" r="6" fill="#000" />
                <circle cx="14" cy="12" r="6" fill="#000" />
              </mask>
              <mask id="rgb-outer-b">
                <rect width="28" height="19" fill="#fff" />
                <circle cx="10" cy="7" r="6" fill="#000" />
                <circle cx="18" cy="7" r="6" fill="#000" />
              </mask>
            </defs>
            <g style={{ mixBlendMode: 'screen' }}>
              <circle cx="10" cy="7" r="6" fill="#ff0000" />
              <circle cx="18" cy="7" r="6" fill="#00ff00" />
              <circle cx="14" cy="12" r="6" fill="#0000ff" />
            </g>
            <g fill="none" stroke={scheme.text} strokeWidth="1">
              <circle cx="10" cy="7" r="6" mask="url(#rgb-outer-r)" />
              <circle cx="18" cy="7" r="6" mask="url(#rgb-outer-g)" />
              <circle cx="14" cy="12" r="6" mask="url(#rgb-outer-b)" />
            </g>
          </svg>
        </button>
      )}
      {expanded && (
        <input
          className="nav-slider"
          type="range"
          title="Fade to black"
          min={0}
          max={1}
          step={0.01}
          value={overlay}
          onChange={(e) => dispatch(setOverlay(parseFloat(e.target.value)))}
          style={{ flex: '0 0 auto', width: '5rem', accentColor: scheme.text }}
        />
      )}
      <Player expanded={expanded} compact={isMobile} color={scheme.text} />
    </div>
  );
};

export default NavBar;
